import { useEffect, useMemo } from 'react';

import { useAppSelector, useAppDispatch } from 'store/store-hooks';
import { setRecentAction } from 'components/app/_mediator/app-logger.slice';
import { selectFcThumbnailData } from 'components/app/_mediator/clip-thumb/clip-thumb.slice';
import { getMarkersEnabledRange } from 'components/app/_mediator/range-markers/range-markers.helper';
import { useClipThumbPrevContext } from 'components/app/_mediator/clip-thumb-prev.provider';
import { EMPTY_CELL } from 'components/app/editor/_mediator/editor.slice';
import { useFinalcutCb } from 'components/app/finalcuts/_mediator/use-finalcut-cb';
import { setFcErrorClips } from 'components/app/finalcuts/_mediator/fc-data.slice';

import { TrackCell } from 'components/app/app-components/track-container/track-elements';
import { ClipUi } from 'components/app/app-components/track-clip/clip-ui';
import { ClipElement } from 'components/app/app-components/track-clip/clip-element';
import { ClipContainer } from 'components/app/app-components/track-clip/clip-container';
import { ClipRatioContainer } from 'components/app/app-components/track-clip/clip-ratio-container';
import { ClipLinking } from 'components/app/app-components/track-clip/clip-linking';
import { ClipPoster } from 'components/app/app-components/track-clip/clip-poster';

interface Props {
  clipId: CellClip;
  width: number;
  index: Index;
}

export const FinalcutsCell = ({ width, clipId, index }: Props) => {
  const dispatch = useAppDispatch();

  const { fcErrorClips, fcClipsMap, fcStoryConfig, fcMarkers, fcFileConfigsMap, id } = useAppSelector(
    (state) => state.fc__data
  );
  const { fcActiveClip } = useAppSelector((state) => state.fc__controls);

  const { setHoveredClip } = useClipThumbPrevContext();
  const { fcSelectClip } = useFinalcutCb();

  const clipData = useMemo(() => {
    if (!fcClipsMap || clipId === EMPTY_CELL) return;
    return fcClipsMap[clipId];
  }, [fcClipsMap, clipId]);

  const enabledRange = useMemo(
    () => getMarkersEnabledRange(fcStoryConfig, fcMarkers?.length ?? 0, 'clip'),
    [fcStoryConfig?.finishMarkerId, fcStoryConfig?.startMarkerId, fcMarkers]
  );

  const handleSelect = () => {
    if (clipId === EMPTY_CELL) return;
    dispatch(setRecentAction({ key: 'USER', desc: `fcCell: handleSelect`, payload: { clipId } }));
    fcSelectClip(index);
  };

  useEffect(() => {
    if (clipId !== EMPTY_CELL && !clipData)
      dispatch(setFcErrorClips({ clipId, message: `ERROR: clip not found: ${clipId}` }));
  }, [clipData]);

  return (
    <TrackCell width={width} onMouseEnter={() => setHoveredClip(clipId)} onClick={handleSelect}>
      <ClipContainer
        modifiers={{
          active: !!fcActiveClip && fcActiveClip === clipId,
          outOfRange: !enabledRange.includes(index) && !!clipId,
          error: !!(clipId && fcErrorClips[clipId]),
          empty: clipId === EMPTY_CELL,
        }}
      >
        {clipData && (
          <ClipElement>
            <ClipRatioContainer>
              <ClipUi clipData={clipData} />
              <ClipPoster
                clipData={clipData}
                fileConfig={fcFileConfigsMap[clipData.mediaFileId]}
                thumbnail={selectFcThumbnailData(clipData)}
                finalCutId={id!}
              />
            </ClipRatioContainer>
            <ClipLinking clipData={clipData} />
          </ClipElement>
        )}
      </ClipContainer>
    </TrackCell>
  );
};
